/**
 * Everything under the ranking on the reveal surface (DESIGN.md §3.6): the
 * question rating, the turn action, and the viewer's own score.
 *
 * Shared by both routes, and identical on both, because the cut between them
 * happens while someone is mid-sentence. The only thing that changes across it
 * is that End turn starts being accepted (see `useEndTurn`).
 */
import type { ScoreDelta, ThumbsVote } from '@idgame/protocol';
import { Button } from '../ui.js';
import { ThumbsWidget } from '../ThumbsWidget.js';

export interface RevealFooterProps {
  turnPlayerName: string;
  isTurnPlayer: boolean;
  isHost: boolean;
  /** Null on a phone that reconnected after the reveal it never saw. */
  delta: ScoreDelta | null;
  vote: ThumbsVote | null;
  voted: boolean;
  onVote: (vote: ThumbsVote) => void;
  endRequested: boolean;
  onEndTurn: () => void;
  advanced: boolean;
  onForceAdvance: () => void;
}

export function RevealFooter({
  turnPlayerName,
  isTurnPlayer,
  isHost,
  delta,
  vote,
  voted,
  onVote,
  endRequested,
  onEndTurn,
  advanced,
  onForceAdvance,
}: RevealFooterProps) {
  const score = scoreLine(delta, isTurnPlayer);
  return (
    <div className="space-y-1.5">
      <ThumbsWidget vote={vote} voted={voted} onVote={onVote} />

      {isTurnPlayer ? (
        <Button data-testid="justification-done" disabled={endRequested} onClick={onEndTurn}>
          End turn
        </Button>
      ) : (
        <p className="py-2 text-center text-[0.8125rem] text-ink-soft">
          Waiting for {turnPlayerName} to end the turn
        </p>
      )}

      {/*
        DESIGN.md §4: the host can force-advance during reveal, but the ordinary
        End turn belongs to the active player — so this is a quiet text action
        under the waiting line, never a second button competing with it.
      */}
      {!isTurnPlayer && isHost && (
        <Button
          variant="quiet"
          data-testid="force-advance"
          disabled={advanced}
          onClick={onForceAdvance}
        >
          Move it along
        </Button>
      )}

      {score && (
        <p
          data-testid="turn-score"
          className="text-center text-[0.6875rem] leading-none font-semibold text-green"
        >
          {score}
        </p>
      )}
    </div>
  );
}

/**
 * "YOU +3 · correct first". The turn player authors the answer and scores
 * nothing on their own turn (SPEC §2.4), which the line says plainly rather than
 * leaving them to wonder where their points went.
 */
export function scoreLine(delta: ScoreDelta | null, isTurnPlayer: boolean): string | null {
  if (isTurnPlayer) return 'YOU +0 · your own turn';
  if (!delta) return null;
  const points = `YOU +${delta.points}`;
  if (delta.perfectRead) return `${points} · both ends`;
  if (delta.correctTop) return `${points} · correct first`;
  if (delta.correctBottom) return `${points} · correct last`;
  return `${points} · no calls landed`;
}
